import { isAxiosError } from 'axios';
import { auth0TokenClient } from 'services/auth0-token-client';
import {
  AUTH0_API_AUDIENCE,
  AUTH0_OAUTH_TOKEN_URL
} from 'constants/auth.constant';
import { AUTH_EVENT } from 'constants/events';
import { HTTP_STATS_CODE, LOCAL_STORAGE } from 'constants/general';
import { AUTH0_OAUTH_GRANT_TYPE } from 'enums';
import { IAuth0RefreshTokenResponse } from 'interfaces/auth.interface';
import { captureException } from 'services/posthog.service';
import {
  getLocalStorage,
  removeLocalStorage,
  updateLocalStorage
} from 'utils/helpers/storage';

const clearStoredRefreshToken = (): void => {
  removeLocalStorage([LOCAL_STORAGE.AUTH_BEARER_REFRESH]);
};

const buildRefreshTokenPayload = (refreshToken: string) => ({
  grant_type: AUTH0_OAUTH_GRANT_TYPE.REFRESH_TOKEN,
  client_id: process.env.REACT_APP_AUTH0_CLIENT_ID ?? '',
  refresh_token: refreshToken,
  audience: AUTH0_API_AUDIENCE
});

/** Auth0 marks the refresh grant unusable with 400 / 401 / 403 (e.g. invalid_grant). */
export const isRefreshTokenRejected = (error: unknown): boolean => {
  if (!isAxiosError(error) || error.response == null) {
    return false;
  }

  return [
    HTTP_STATS_CODE.BAD_REQUEST,
    HTTP_STATS_CODE.UNAUTHORIZED,
    HTTP_STATS_CODE.FORBIDDEN
  ].includes(error.response.status);
};

export const storeAuthTokensFromGrant = (
  response: IAuth0RefreshTokenResponse,
  shouldDispatchEvent: boolean
): void => {
  updateLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN, response.access_token);

  if (response.id_token) {
    updateLocalStorage(LOCAL_STORAGE.AUTH_ID, response.id_token);
  }

  if (response.refresh_token) {
    updateLocalStorage(
      LOCAL_STORAGE.AUTH_BEARER_REFRESH,
      response.refresh_token
    );
  }

  if (shouldDispatchEvent) {
    window.dispatchEvent(new Event(AUTH_EVENT.UPDATED, { composed: true }));
  }
};

const applyRefreshGrantResponse = (
  data: IAuth0RefreshTokenResponse,
  shouldDispatchEvent: boolean
): boolean => {
  if (!data.access_token) {
    clearStoredRefreshToken();
    return false;
  }

  storeAuthTokensFromGrant(data, shouldDispatchEvent);
  return true;
};

/**
 * Exchanges the stored refresh token for a new access token via Auth0's
 * OAuth refresh_token grant (POST /oauth/token). Prefer this over silent
 * iframe renewAuth — it works without third-party cookies.
 */
export const exchangeRefreshTokenGrant = async (
  shouldDispatchEvent = true
): Promise<boolean> => {
  const refreshToken = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_REFRESH);
  if (!refreshToken) {
    return false;
  }

  try {
    const { data } = await auth0TokenClient.post<IAuth0RefreshTokenResponse>(
      AUTH0_OAUTH_TOKEN_URL,
      buildRefreshTokenPayload(refreshToken)
    );
    return applyRefreshGrantResponse(data, shouldDispatchEvent);
  } catch (error) {
    captureException('Token refresh failed', { error });
    if (isRefreshTokenRejected(error)) {
      clearStoredRefreshToken();
    }
    return false;
  }
};
